import { useCallback, useMemo } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { IContact, TContactCommonForm } from 'clickNgo/entities/contact'
import { validateContact } from 'clickNgo/entities/contact/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useGetFormData, useGetFormDataField } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers'
import { TWizardStep } from 'shared/components/wizard'

import {
  ContactCreatorFormKeys,
  ContactCreatorSteps,
  CreateContactFormContent,
} from '../../../../configs'
import { IContactFormKeys } from '../../../../models'
import { CommonStep } from '../CommonStep'

import { useCommonStepValidation } from './useCommonStepValidation'

interface IParams {
  formKeys: IContactFormKeys
  contact: IContact
  partner: IPartner
}

export const useCommonStep = ({ formKeys, contact, partner }: IParams) => {
  const validationProps = useCommonStepValidation()
  const [{ formData }] = useGetFormData<TContactCommonForm>(formKeys.common)

  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactCreatorFormKeys.Settings,
    CreateContactFormContent.fields.errorMessages.field
  )

  const content = useCallback(
    () => <CommonStep contact={contact} formKeys={formKeys} {...validationProps} />,
    [contact, formKeys, validationProps]
  )

  const handleOnStepChange = useCallback(
    async (prevStep: number, nextStep: number) => {
      setErrorMessages([])
      const isStepBack = prevStep > nextStep

      if (isStepBack) {
        return
      }

      const validationResult = await validateFormData(formData, ContactCreatorFormKeys.Common)

      if (validationResult && validationResult.length > 0) {
        return {
          hasError: true,
        }
      }

      const validateAsyncResult = await validateContact({
        contactId: contact.contactId,
        partnerId: contact.partnerId,
        country: partner.country,
        ...formData,
      })

      if (validateAsyncResult.status === OnboardingResponseStatus.Fail) {
        setErrorMessages(getJSendErrorMessages(validateAsyncResult))
        return {
          hasError: true,
        }
      }

      if (validateAsyncResult.status === OnboardingResponseStatus.Error) {
        setErrorMessages([validateAsyncResult.message])
        return {
          hasError: true,
        }
      }

      return
    },
    [formData, contact.contactId, contact.partnerId, partner.country, setErrorMessages]
  )

  const additionalStep = useMemo<TWizardStep>(
    () => ({
      id: ContactCreatorSteps.Common,
      title: ContactCreatorSteps.Common,
      content,
      onStepChange: handleOnStepChange,
    }),
    [handleOnStepChange, content]
  )

  return { additionalStep }
}
